import { Component, OnInit, inject } from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
  MatDialogContent,
  MatDialogActions
} from "@angular/material/dialog";
import { ResourceAvailabilityStore } from "@app/services/store/resource-availability-store";
import {
  ResourceAvailability,
  ResourceAvailability_DetailProjection
} from "@app/models/rest/resource-availability";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  FormsModule,
  ReactiveFormsModule
} from "@angular/forms";
import { AvailabilityCodeStore } from "@app/services/store/availability-code-store";
import { AvailabilityCode } from "@app/models/rest/availability-code";
import { MatSelectChange, MatSelect } from "@angular/material/select";
import moment from "moment";
import { firstValueFrom } from "rxjs";
import { AvailabilityDeleteConfirmDialogComponent } from "@app/view/diary/availability-delete-confirm-dialog/availability-delete-confirm-dialog.component";
import { ResourceModel } from "@app/models/rest/resource";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import { AvailabilityType } from "@app/models/enums/availability-type.enum";
import {
  MatSliderChange,
  MatSlider,
  MatSliderThumb
} from "@angular/material/slider";
import { select, Store } from "@ngrx/store";
import { selectorAuth } from "@app/core";
import { ResourceStore } from "@app/services/store/resource-store";
import { ResourceAvailabilityAuditViewerComponent } from "@app/view/shared/audits/resource-availability-audit-viewer/resource-availability-audit-viewer.component";
import { TimesheetCode } from "@app/models/rest/timesheet-code";
import { TimesheetCodeStore } from "@app/services/store/timesheet-code-store";
import { ClientConfigService } from "@app/core/config/client-config.service";
import { SolverService } from "@app/services/rest/solver.service";
import { ResourceAvailabilityDto } from "@app/models/dto/resource-availability-dto";
import { ZoneStore } from "@app/services/store/zone-store";
import { SkillStore } from "@app/services/store/skill-store";
import { Zone } from "@app/models/rest/zone";
import { Skill } from "@app/models/rest/skill";
import { MatToolbar, MatToolbarRow } from "@angular/material/toolbar";
import { CdkDrag, CdkDragHandle } from "@angular/cdk/drag-drop";
import { MatIconButton, MatButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { NgxUiLoaderModule } from "ngx-ui-loader";
import { CdkScrollable } from "@angular/cdk/scrolling";
import {
  MatFormField,
  MatLabel,
  MatSuffix
} from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker
} from "@angular/material/datepicker";
import { MatOption } from "@angular/material/core";
import { TimesheetCodeChooserComponent } from "../../shared/timesheet-code-chooser/timesheet-code-chooser.component";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { TranslatePipe } from "@ngx-translate/core";
import { DialogHeaderComponent } from "../../shared/dialog-header/dialog-header.component";

export class LiveUpdateData {
  newStart: Date;
  newEnd: Date;
  newAvailabilityCode: AvailabilityCode;
  newDescription: string;
}

@Component({
  selector: "app-availability-editor",
  templateUrl: "./availability-editor.component.html",
  styleUrls: ["./availability-editor.component.scss"],
  imports: [
    MatIcon,
    NgxUiLoaderModule,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatSelect,
    MatOption,
    MatSlider,
    MatSliderThumb,
    TimesheetCodeChooserComponent,
    MatProgressSpinner,
    MatDialogActions,
    MatButton,
    TranslatePipe,
    DialogHeaderComponent
  ]
})
export class AvailabilityEditorComponent implements OnInit {
  resourceAvailabilityStore = inject(ResourceAvailabilityStore);
  resourceStore = inject(ResourceStore);
  timesheetCodeStore = inject(TimesheetCodeStore);
  availabilityCodeStore = inject(AvailabilityCodeStore);
  dialogRef = inject<MatDialogRef<AvailabilityEditorComponent>>(MatDialogRef);
  dialog = inject(MatDialog);
  protected clientConfig = inject(ClientConfigService);
  serverErrorHandler = inject(ServiceErrorHandlerService);
  fb = inject(UntypedFormBuilder);
  store = inject<Store<any>>(Store);
  config = inject(ClientConfigService);
  private solverService = inject(SolverService);
  private zoneStore = inject(ZoneStore);
  private skillStore = inject(SkillStore);
  data = inject<{
    resourceAvailabilityId: number;
    initialResourceId: number;
    initialStart: Date;
    initialEnd: Date;
    liveUpdateCallback: Function;
  }>(MAT_DIALOG_DATA);

  public resourceAvailability: ResourceAvailability;
  public resourceAvailabilityForm: UntypedFormGroup;
  public availabilityCodes: AvailabilityCode[];
  public loaded: boolean = false;

  //sliders
  public cyclicalPriorityReservationPercent: number;
  public normalPriorityReservationPercent: number;
  public highPriorityReservationPercent: number;
  public emergencyPriorityReservationPercent: number;
  sliderConfig = {
    cyclical: {
      value: "cyclicalPriorityReservationPercent",
      higherThan: [],
      lowerThan: ["normal", "high", "emergency"]
    },
    normal: {
      value: "normalPriorityReservationPercent",
      higherThan: ["cyclical"],
      lowerThan: ["high", "emergency"]
    },
    high: {
      value: "highPriorityReservationPercent",
      higherThan: ["cyclical", "normal"],
      lowerThan: ["emergency"]
    },
    emergency: {
      value: "emergencyPriorityReservationPercent",
      higherThan: ["cyclical", "normal", "high"],
      lowerThan: []
    }
  };

  public isAssignType: boolean;
  public isBlockType: boolean;
  public canEdit: boolean = false;
  public allowAudit: boolean = false;
  public allowPriorityReservation: boolean = false;
  public timesheetCodes: TimesheetCode[];
  public selectedTravelCodeLoaded: boolean;
  public selectedTravelCode: TimesheetCode;
  public selectedWorkCodeLoaded: boolean;
  public selectedWorkCode: TimesheetCode;
  public selectedOverCodeLoaded: boolean;
  public selectedOverCode: TimesheetCode;
  public timesheetCodeMode: boolean;
  public effectiveSkills: {
    id: number;
    name?: string;
    description?: string;
  }[] = [];
  public effectiveZones: { id: number; name?: string; description?: string }[] =
    [];
  public effectiveDataLoaded: boolean = false;
  public effectiveDataLoading: boolean = false;
  public effectiveDataError: string | null = null;
  private selectedCode: AvailabilityCode;
  private addMode: boolean = false;
  public resource: ResourceModel; // Changed from private to public so it can be accessed in the template

  // Lists of all zones and skills for mapping IDs to names
  private allZones: Zone[] = [];
  private allSkills: Skill[] = [];
  private zonesLoaded: boolean = false;
  private skillsLoaded: boolean = false;

  constructor() {
    const config = this.config;

    this.timesheetCodeMode = config?.config?.enableTimesheetCodes;
    this.resourceAvailabilityForm = this.fb.group({
      start: null,
      end: null,
      startTime: null,
      endTime: null,
      overTime: null,
      overrideTime: null,
      description: null,
      availabilityCode: null,
      workingTimesheetCode: null,
      travelTimesheetCode: null,
      overrideTimesheetCode: null,
      cyclicalPriorityReservationDuration: null,
      normalPriorityReservationDuration: null,
      normalPriorityReservationPercent: null,
      highPriorityReservationDuration: null,
      highPriorityReservationPercent: null,
      emergencyPriorityReservationDuration: null,
      emergencyPriorityReservationPercent: null
    });
    this.resourceAvailabilityForm.valueChanges.subscribe(() => {
      this.liveUpdate();
    });
    this.dialogRef.beforeClosed()?.subscribe(() => {
      console.log("Before closed fire");
      this.resetForm();
    });
    this.store.pipe(select(selectorAuth)).subscribe((auth) => {
      this.allowAudit = !!auth && auth.allowAudit;
      console.log("Allowed audit? " + this.allowAudit);
    });
  }

  resetForm() {
    console.log(
      "Resetting form with override time",
      this.resourceAvailability.overrideTime
    );
    this.selectedOverCode = this.resourceAvailability.overrideTimesheetCode;
    this.selectedWorkCode = this.resourceAvailability.workingTimesheetCode;
    this.selectedTravelCode = this.resourceAvailability.travelTimesheetCode;
    this.resourceAvailabilityForm.patchValue({
      start: this.resourceAvailability.start,
      end: this.resourceAvailability.end,
      startTime: moment(this.resourceAvailability.start)
        .parseZone()
        .format("HH:mm"),
      endTime: moment(this.resourceAvailability.end)
        .parseZone()
        .format("HH:mm"),
      description: this.resourceAvailability.description,
      availabilityCode: this.resourceAvailability.availabilityCode,
      workingTimesheetCode: this.resourceAvailability.workingTimesheetCode,
      travelTimesheetCode: this.resourceAvailability.travelTimesheetCode,
      overrideTimesheetCode: this.resourceAvailability.overrideTimesheetCode,
      overrideTime: this.resourceAvailability.overrideTime,
      overTime: this.resourceAvailability.overrideTime
        ? moment(this.resourceAvailability.overrideTime)
            .parseZone()
            .format("HH:mm")
        : null,
      cyclicalPriorityReservationDuration:
        this.resourceAvailability.cyclicalPriorityReservationDuration,
      cyclicalPriorityReservationPercent:
        this.resourceAvailability.cyclicalPriorityReservationPercent,
      normalPriorityReservationDuration:
        this.resourceAvailability.normalPriorityReservationDuration,
      normalPriorityReservationPercent:
        this.resourceAvailability.normalPriorityReservationPercent,
      highPriorityReservationDuration:
        this.resourceAvailability.highPriorityReservationDuration,
      highPriorityReservationPercent:
        this.resourceAvailability.highPriorityReservationPercent,
      emergencyPriorityReservationDuration:
        this.resourceAvailability.emergencyPriorityReservationDuration,
      emergencyPriorityReservationPercent:
        this.resourceAvailability.emergencyPriorityReservationPercent
    });
    this.cyclicalPriorityReservationPercent =
      this.resourceAvailability.cyclicalPriorityReservationPercent;
    this.normalPriorityReservationPercent =
      this.resourceAvailability.normalPriorityReservationPercent;
    this.highPriorityReservationPercent =
      this.resourceAvailability.highPriorityReservationPercent;
    this.emergencyPriorityReservationPercent =
      this.resourceAvailability.emergencyPriorityReservationPercent;

    this.resourceAvailabilityForm.markAsPristine();
  }

  /**
   * Load the resource relation from the availability
   */
  loadResourceRelation() {
    if (!this.resourceAvailability) {
      return;
    }

    // For new records, the resource is already set directly
    if (this.addMode && this.resourceAvailability.resource) {
      this.resource = this.resourceAvailability.resource;
      console.log("Resource already available for new record:", this.resource);
      return;
    }

    // For existing records, load the resource relation
    this.resourceAvailability.getRelation<ResourceModel>("resource").subscribe(
      (resourceValue) => {
        if (resourceValue instanceof ResourceModel) {
          this.resource = resourceValue;
          console.log("Resource loaded:", this.resource);
        }
      },
      (error) => {
        console.error("Error loading resource relation:", error);
      }
    );
  }

  loadAvailabilityCode() {
    this.resourceAvailability
      .getRelation<AvailabilityCode>("availabilityCode")
      .subscribe(
        (value1) => {
          if (value1 instanceof AvailabilityCode) {
            this.availabilityCodes.forEach((value2) => {
              if (value1.id === value2.id) {
                this.selectedCode = value2;
                this.resourceAvailability.availabilityCode = value2;

                switch (this.selectedCode.availabilityType) {
                  case "ASSIGN":
                    this.isAssignType = true;
                    this.isBlockType = false;
                    break;
                  case "BLOCK":
                    this.isAssignType = false;
                    this.isBlockType = true;
                    break;
                }
              }
            });

            this.resetForm();
            this.loadEffectiveSkillsAndZones();
            this.loaded = true;
          }
        },
        () => {
          // assume a 404
          this.resetForm();
          this.loaded = true;
        }
      );
  }

  /**
   * Load effective skills and zones for the current availability
   */
  async loadEffectiveSkillsAndZones() {
    if (!this.resourceAvailability?.id) {
      this.effectiveDataLoaded = true;
      return;
    }

    this.effectiveDataLoading = true;
    this.effectiveDataError = null;
    this.effectiveDataLoaded = false;
    this.effectiveSkills = [];
    this.effectiveZones = [];

    try {
      // Wait for zones and skills to be loaded if they're not already
      if (!this.zonesLoaded || !this.skillsLoaded) {
        console.log(
          "Waiting for zones and skills to load before processing availability data"
        );
        await new Promise<void>((resolve) => {
          const checkLoaded = () => {
            if (this.zonesLoaded && this.skillsLoaded) {
              resolve();
            } else {
              setTimeout(checkLoaded, 100);
            }
          };
          checkLoaded();
        });
      }

      const availabilityData = await this.solverService.checkAvailability(
        this.resourceAvailability.id
      );

      if (availabilityData) {
        // Process skills
        if (availabilityData.skills && availabilityData.skills.length > 0) {
          this.effectiveSkills = availabilityData.skills.map((skillId) => {
            // Find the skill in the allSkills list
            const skill = this.allSkills.find((s) => s.id === skillId);

            if (skill) {
              return {
                id: skillId,
                name: skill.description, // Using description as name since that's what's displayed in the UI
                description: skill.description
              };
            }

            return { id: skillId, name: `Skill ID: ${skillId}` };
          });
        }

        // Process zones
        if (availabilityData.zones && availabilityData.zones.length > 0) {
          this.effectiveZones = availabilityData.zones.map((zoneId) => {
            // Find the zone in the allZones list
            const zone = this.allZones.find((z) => z.id === zoneId);

            if (zone) {
              return {
                id: zoneId,
                name: zone.name,
                description: zone.area || ""
              };
            }

            return { id: zoneId, name: `Zone ID: ${zoneId}` };
          });
        }
      }
    } catch (error) {
      console.error("Error loading effective skills and zones:", error);
      this.effectiveDataError =
        error instanceof Error
          ? error.message
          : "Failed to load effective skills and zones";
    } finally {
      this.effectiveDataLoading = false;
      this.effectiveDataLoaded = true;
    }
  }

  async loadTimesheetCode(key: string, stateVariable: string) {
    try {
      const timesheetCode = await firstValueFrom(
        this.resourceAvailability.getRelation<TimesheetCode>(key)
      );
      const matchedCode = this.timesheetCodes.find(
        (code) => code.id === timesheetCode.id
      );

      if (matchedCode) {
        this[stateVariable] = matchedCode;
        this.resourceAvailability[key] = matchedCode;
      }
    } catch (error) {
      // Log or handle errors such as a 404
      console.error("Failed to load timesheet code:", error);
    } finally {
      this[stateVariable + "Loaded"] = true; // Ensuring the loaded state is set
    }
  }

  ngOnInit() {
    this.store.pipe(select(selectorAuth)).subscribe((auth) => {
      this.canEdit = !!auth && (auth.isAdmin || auth.isPlanner);
      this.allowPriorityReservation = !!auth && auth.allowPriorityReservation;
    });

    // Load all zones and skills for mapping IDs to names
    this.loadAllZonesAndSkills();

    console.log("Opening dialog with ", this.data.initialResourceId);
    this.availabilityCodeStore
      .loadAll({ params: { projection: "detail" } }, true)
      .then((availabilityCodes) => {
        this.availabilityCodes = availabilityCodes.resources;
        if (this.data.resourceAvailabilityId) {
          this.resourceAvailabilityStore
            .loadSingle(this.data.resourceAvailabilityId)
            .then((value) => {
              this.resourceAvailability = value;
              console.log(value);
              // Load the resource relation
              this.loadResourceRelation();
              this.loadAvailabilityCode();
              // Load effective skills and zones regardless of availability code
              this.loadEffectiveSkillsAndZones();
              this.timesheetCodeStore.loadAll().then((value) => {
                this.timesheetCodes = value.resources;
                if (this.timesheetCodeMode) {
                  this.loadTimesheetCode(
                    "travelTimesheetCode",
                    "selectedTravelCode"
                  );
                  this.loadTimesheetCode(
                    "workingTimesheetCode",
                    "selectedWorkCode"
                  );
                  this.loadTimesheetCode(
                    "overrideTimesheetCode",
                    "selectedOverCode"
                  );
                }
              });
            });
        } else {
          this.resourceStore
            .loadSingle(this.data.initialResourceId)
            .then((resource) => {
              this.resourceAvailability = new ResourceAvailability();
              this.resourceAvailability.start = this.data.initialStart;
              this.resourceAvailability.end = this.data.initialEnd;
              this.resourceAvailability.resource = resource;

              this.resourceAvailability.cyclicalPriorityReservationPercent = 0;
              this.resourceAvailability.normalPriorityReservationPercent = 0;
              this.resourceAvailability.highPriorityReservationPercent = 0;
              this.resourceAvailability.emergencyPriorityReservationPercent = 0;
              this.addMode = true;
              // Load the resource relation for new records too
              this.loadResourceRelation();
              this.resetForm();
              this.loaded = true;
            });
        }
      });
  }

  /**
   * Load all zones and skills for mapping IDs to names
   */
  private loadAllZonesAndSkills(): void {
    // Load all zones with detail projection
    this.zoneStore
      .loadAll({ params: { projection: "detail" } })
      .then((result) => {
        this.allZones = result.resources;
        this.zonesLoaded = true;
        console.log("Loaded all zones:", this.allZones.length);
      })
      .catch((error) => {
        console.error("Error loading zones:", error);
        this.zonesLoaded = true; // Mark as loaded even on error to prevent blocking
      });

    // Load all skills
    this.skillStore
      .loadAll()
      .then((result) => {
        this.allSkills = result.resources;
        this.skillsLoaded = true;
        console.log("Loaded all skills:", this.allSkills.length);
      })
      .catch((error) => {
        console.error("Error loading skills:", error);
        this.skillsLoaded = true; // Mark as loaded even on error to prevent blocking
      });
  }

  onCancelClick() {
    this.dialogRef.close();
  }

  onDeleteClick() {
    let deleteRef = this.dialog.open(
      AvailabilityDeleteConfirmDialogComponent,
      {}
    );
    deleteRef.afterClosed().subscribe((dialogResponse) => {
      console.log("delete dialog response", dialogResponse);
      if (dialogResponse.deleted) {
        this.resourceAvailabilityStore
          .delete(this.resourceAvailability)
          .subscribe(
            () => {
              console.log("Server deletion completed");
              this.retrieveFullRecordAndClose(true, true, null);
            },
            (error1) => {
              this.serverErrorHandler.handleError(error1);
            }
          );
      }
    });
  }

  liveUpdate() {
    if (this.data.liveUpdateCallback) {
      let startTime = this.resourceAvailabilityForm.value.startTime;
      let endTime = this.resourceAvailabilityForm.value.endTime;

      let processedStartTime = moment(startTime, "HH:mm");
      let processedEndTime = moment(endTime, "HH:mm");

      let startMoment = moment(this.resourceAvailabilityForm.value.start);
      let endMoment = moment(this.resourceAvailabilityForm.value.end);

      startMoment.hour(processedStartTime.hour());
      startMoment.minute(processedStartTime.minute());

      endMoment.hour(processedEndTime.hour());
      endMoment.minute(processedEndTime.minute());

      if (endMoment.isBefore(startMoment)) {
        endMoment = endMoment.add(1, "day");
      }

      let liveUpdate: LiveUpdateData = {
        newDescription: this.resourceAvailabilityForm.value.description,
        newStart: startMoment.toDate(),
        newEnd: endMoment.toDate(),
        newAvailabilityCode:
          this.resourceAvailabilityForm.value.availabilityCode
      };
      this.data.liveUpdateCallback({ data: liveUpdate });
    }
  }

  async save() {
    //  Date picker selected date
    let selectedDate = moment(this.resourceAvailability.end);
    let maxPlanningWindow: number = this.clientConfig.config.maxPlanningWindow;
    let todayPlusMaxPlanningWindow = moment().add(maxPlanningWindow, "days");

    // Diary availability validation
    if (
      maxPlanningWindow != 0 &&
      selectedDate.isAfter(todayPlusMaxPlanningWindow) &&
      this.selectedCode?.availabilityType !== "BLOCK"
    ) {
      this.serverErrorHandler.handleClientError(
        `The selected date is beyond ${maxPlanningWindow} days and has an invalid availability code.`
      );
      return;
    } else {
      console.log(
        `The selected date is within ${maxPlanningWindow} days from today/contains a valid code.`
      );
    }

    this.prepareResourceAvailabilityData();
    try {
      const updatedResourceAvailability = await firstValueFrom(
        this.upsertResourceAvailability()
      );
      this.resourceAvailability = updatedResourceAvailability;
      await this.updateAvailabilityCode();
      if (this.timesheetCodeMode) {
        await this.updateTimesheetCode(
          "travelTimesheetCode",
          this.selectedTravelCode
        );
        await this.updateTimesheetCode(
          "workingTimesheetCode",
          this.selectedWorkCode
        );
        await this.updateTimesheetCode(
          "overrideTimesheetCode",
          this.selectedOverCode
        );
      }
      this.retrieveFullRecordAndClose(true, false, this.resourceAvailability);
    } catch (error) {
      this.serverErrorHandler.handleError(error);
    }
  }

  prepareResourceAvailabilityData() {
    const { startTime, endTime, overTime } =
      this.resourceAvailabilityForm.value;
    const processedStartTime = moment(startTime, "HH:mm");
    const processedEndTime = moment(endTime, "HH:mm");
    const processedOverTime = moment(overTime, "HH:mm");

    let startMoment = moment(this.resourceAvailabilityForm.value.start);
    let endMoment = moment(this.resourceAvailabilityForm.value.end);
    let overMoment = moment(
      this.resourceAvailabilityForm.value.overrideTime ??
        this.resourceAvailabilityForm.value.start
    );

    startMoment.hour(processedStartTime.hour());
    startMoment.minute(processedStartTime.minute());
    endMoment.hour(processedEndTime.hour());
    endMoment.minute(processedEndTime.minute());
    overMoment.hour(processedOverTime.hour());
    overMoment.minute(processedOverTime.minute());

    if (endMoment.isBefore(startMoment)) {
      endMoment.add(1, "day");
    }
    if (overMoment.isBefore(startMoment)) {
      overMoment.add(1, "day");
    }

    this.resourceAvailability.start = startMoment.toDate();
    this.resourceAvailability.end = endMoment.toDate();
    this.resourceAvailability.overrideTime = overMoment.toDate();

    this.resourceAvailability.cyclicalPriorityReservationPercent =
      this.cyclicalPriorityReservationPercent;
    this.resourceAvailability.normalPriorityReservationPercent =
      this.normalPriorityReservationPercent;
    this.resourceAvailability.highPriorityReservationPercent =
      this.highPriorityReservationPercent;
    this.resourceAvailability.emergencyPriorityReservationPercent =
      this.emergencyPriorityReservationPercent;
    this.resourceAvailability.description =
      this.resourceAvailabilityForm.controls["description"].value;
  }

  upsertResourceAvailability() {
    return this.addMode
      ? this.resourceAvailabilityStore.create(this.resourceAvailability)
      : this.resourceAvailabilityStore.update(this.resourceAvailability);
  }

  async updateAvailabilityCode() {
    try {
      if (this.selectedCode) {
        await firstValueFrom(
          this.resourceAvailability.bindRelation(
            "availabilityCode",
            this.selectedCode
          )
        );
      } else if (this.resourceAvailability.availabilityCode) {
        await firstValueFrom(
          this.resourceAvailability.deleteRelation(
            "availabilityCode",
            this.resourceAvailability.availabilityCode
          )
        );
      }
    } catch (error) {
      this.serverErrorHandler.handleError(error);
    }
  }

  async updateTimesheetCode(relationKey: string, selectedCode: TimesheetCode) {
    try {
      const currentCode = this.resourceAvailability[relationKey];
      if (selectedCode) {
        await firstValueFrom(
          this.resourceAvailability.bindRelation(relationKey, selectedCode)
        );
      } else if (currentCode) {
        await firstValueFrom(
          this.resourceAvailability.deleteRelation(relationKey, currentCode)
        );
      }
    } catch (error) {
      this.serverErrorHandler.handleError(error);
    }
  }

  retrieveFullRecordAndClose(completed, deleted, newRecord) {
    if (deleted) {
      this.dialogRef.close({ wasDeleted: deleted });
    } else {
      this.resourceAvailabilityStore
        .loadSingleProjection(
          ResourceAvailability_DetailProjection,
          newRecord.id,
          "detail"
        )
        .then((value1) => {
          console.log("Closing dialog with ", value1);
          this.dialogRef.close({
            completed: completed,
            wasDeleted: deleted,
            newRecord: value1
          });
        });
    }
  }

  formatLabel(value: number) {
    return value + "%";
  }

  onSliderChange(sliderName: string, value: number) {
    const slider = this.sliderConfig[sliderName];
    this[slider.value] = value;
    for (const higher of slider.higherThan) {
      if (this[this.sliderConfig[higher].value] > value) {
        this[this.sliderConfig[higher].value] = value;
      }
    }
    for (const lower of slider.lowerThan) {
      if (this[this.sliderConfig[lower].value] < value) {
        this[this.sliderConfig[lower].value] = value;
      }
    }
    this.resourceAvailabilityForm.markAsDirty();
  }

  startTimeChanged($event: Event) {
    console.log($event);
  }

  selectedCodeChange($event: MatSelectChange) {
    console.log("code change", $event.value);
    this.selectedCode = $event.value;
    this.isAssignType = false;
    this.isBlockType = false;
    if (!this.selectedCode) {
      return;
    }

    switch ($event.value.availabilityType) {
      case "ASSIGN":
        this.isAssignType = true;
        this.isBlockType = false;
        break;
      case "BLOCK":
        this.isAssignType = false;
        this.isBlockType = true;
        break;
    }
    this.resourceAvailabilityForm.patchValue({
      availabilityCode: this.selectedCode
    });
    this.liveUpdate();
  }

  public testIsBlockType(avType: AvailabilityType): boolean {
    return avType === AvailabilityType.BLOCK;
  }

  openAudit() {
    this.dialog.open(ResourceAvailabilityAuditViewerComponent, {
      width: "calc(100vw - 100px)",
      data: {
        availabilityId: this.resourceAvailability.id
      }
    });
  }

  handleWorkCodeChange($event: TimesheetCode) {
    this.resourceAvailabilityForm.markAsDirty();
    this.selectedWorkCode = $event;
  }

  handleTravelCodeChange($event: TimesheetCode) {
    this.resourceAvailabilityForm.markAsDirty();
    this.selectedTravelCode = $event;
  }

  handleOverCodeChange($event: TimesheetCode) {
    this.resourceAvailabilityForm.markAsDirty();
    this.selectedOverCode = $event;
  }

  overTimeChanged($event: Event) {
    this.resourceAvailabilityForm.markAsDirty();
    console.log("Overtime changed");
  }
}
